import { useState } from "react";
import { Instagram, Facebook, Twitter, Youtube, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { EVENT_CONFIG, API } from "@/config/eventConfig";

const Footer = () => {
  const { org, event, urls } = EVENT_CONFIG;
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await axios.post(`${API}/notify`, { email, source: "footer" });
      toast.success("You're on the list — gala updates coming your way.");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email address.");
    } finally {
      setSending(false);
    }
  };

  const socials = [
    { icon: Instagram, href: org.socials.instagram, label: "Instagram" },
    { icon: Facebook, href: org.socials.facebook, label: "Facebook" },
    { icon: Twitter, href: org.socials.twitter, label: "Twitter" },
    { icon: Youtube, href: org.socials.youtube, label: "YouTube" },
  ];

  return (
    <footer data-testid="footer" className="relative bg-coal border-t border-champagne/15 grain">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col gap-5">
          <img src={EVENT_CONFIG.images.logo} alt="Styles 4 Kidz Gala" className="w-48 rounded-lg" loading="lazy" />
          <p className="text-cream/70 text-sm leading-relaxed">{org.mission}</p>
          <div className="flex gap-3">
            {socials.map((s) => (
              <a
                key={s.label}
                data-testid={`footer-social-${s.label.toLowerCase()}`}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="w-9 h-9 rounded-full border border-champagne/30 flex items-center justify-center text-champagne hover:bg-champagne hover:text-ink transition-colors"
              >
                <s.icon size={15} />
              </a>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Visit</p>
          {[
            { label: "The Mission", href: "#mission" },
            { label: "The Gala", href: "#gala" },
            { label: "Adult Prom", href: "#adult-prom" },
            { label: "Tickets", href: "#tickets" },
            { label: "Sponsor", href: "#sponsor" },
            { label: "After Party", href: "#after-party" },
          ].map((l) => (
            <a key={l.href} href={l.href} className="text-cream/70 hover:text-champagne text-sm transition-colors">
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex flex-col gap-4">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Contact</p>
          <a href={org.website} target="_blank" rel="noopener noreferrer" className="text-cream/70 hover:text-champagne text-sm transition-colors">
            styles4kidz.org
          </a>
          <a href={`mailto:${org.email}`} className="flex items-center gap-2 text-cream/70 hover:text-champagne text-sm transition-colors">
            <Mail size={14} className="text-gold" /> {org.email}
          </a>
          <a href={`tel:${org.phone}`} className="flex items-center gap-2 text-cream/70 hover:text-champagne text-sm transition-colors">
            <Phone size={14} className="text-gold" /> {org.phone}
          </a>
          <p className="flex items-start gap-2 text-cream/70 text-sm">
            <MapPin size={14} className="text-gold mt-0.5 shrink-0" /> {org.address}
          </p>
          <p className="text-cream/50 text-xs leading-relaxed">
            {event.title} {event.titleAccent} · {event.dateDisplay} · {event.venue}, {event.city}
          </p>
        </div>

        <div className="flex flex-col gap-4">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Get Gala Updates</p>
          <p className="text-cream/60 text-sm">Ticket drops, after-party news and crown-worthy announcements.</p>
          <form onSubmit={subscribe} className="flex flex-col gap-3" data-testid="footer-newsletter-form">
            <input
              data-testid="footer-newsletter-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              className="bg-ink/70 border border-white/10 rounded-xl px-5 py-3.5 text-ivory placeholder:text-cream/40 focus:outline-none focus:border-champagne/60"
            />
            <button
              data-testid="footer-newsletter-submit-button"
              type="submit"
              disabled={sending}
              className="py-3.5 rounded-xl bg-gradient-to-r from-gold via-champagne to-gold text-ink text-xs uppercase tracking-[0.2em] font-bold hover:shadow-[0_10px_30px_-8px_rgba(230,199,134,0.6)] transition-shadow disabled:opacity-60"
            >
              {sending ? "Joining…" : "Join the List"}
            </button>
          </form>
          <a
            data-testid="footer-donate-link"
            href={urls.donate}
            target="_blank"
            rel="noopener noreferrer"
            className="text-champagne text-sm hover:text-ivory transition-colors"
          >
            Donate to Styles 4 Kidz →
          </a>
        </div>
      </div>

      <div className="border-t border-champagne/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-cream/40 text-xs tracking-wide text-center sm:text-left">
            © 2026 {org.name} · {event.city} · Gala photography: Jamilla Yipp Photography
          </p>
          <div className="flex gap-6">
            <a href="#home" data-testid="footer-privacy-link" className="text-cream/40 hover:text-champagne text-xs transition-colors">
              Privacy Policy
            </a>
            <a href="#home" data-testid="footer-terms-link" className="text-cream/40 hover:text-champagne text-xs transition-colors">
              Terms
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
